import { validateTemplate } from '@/domain/template';
import type { ProblemTemplate } from '@/domain/types';
import {
  collectWarnings,
  firstPartBlockSchema,
  issuesFromZod,
  laterPartBlockSchema,
  type ImportIssue,
  type ValidatedBlock,
} from './schema';

/**
 * Turns pasted text into validated blocks. Tolerates a markdown code fence and
 * several blocks pasted in one go, and reports every failure with its path.
 */

export interface BlockParseResult {
  /** 1-based position in the paste. */
  index: number;
  ok: boolean;
  block?: ValidatedBlock;
  errors: ImportIssue[];
  warnings: ImportIssue[];
}

export interface ParseResult {
  results: BlockParseResult[];
  /** Only the blocks that validated, in paste order. */
  blocks: ValidatedBlock[];
  errors: ImportIssue[];
  warnings: ImportIssue[];
}

const FENCE_LINE = /^\s*```.*$/gm;
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/** JSON.parse that drops prototype-polluting keys outright. */
export function safeJsonParse(text: string): unknown {
  return JSON.parse(text, function reviver(key, value) {
    if (DANGEROUS_KEYS.has(key)) return undefined;
    return value;
  });
}

/**
 * Finds balanced top-level `{...}` objects, ignoring braces inside strings.
 * Prose or fences between blocks are skipped.
 */
export function extractJsonCandidates(input: string): string[] {
  const text = input.replace(FENCE_LINE, '');
  const out: string[] = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === '}') {
      if (depth > 0) {
        depth -= 1;
        if (depth === 0 && start >= 0) {
          out.push(text.slice(start, i + 1));
          start = -1;
        }
      }
    }
  }
  return out;
}

function allIds(block: ValidatedBlock): Array<{ id: string; path: string }> {
  const out: Array<{ id: string; path: string }> = [];
  block.flashcards.forEach((f, i) => out.push({ id: f.id, path: `flashcards[${i}].id` }));
  block.questions.forEach((q, i) => out.push({ id: q.id, path: `questions[${i}].id` }));
  block.problemTemplates.forEach((t, i) => out.push({ id: t.id, path: `problemTemplates[${i}].id` }));
  block.formulas.forEach((f, i) => out.push({ id: f.id, path: `reference.formulas[${i}].id` }));
  block.constants.forEach((c, i) => out.push({ id: c.id, path: `reference.constants[${i}].id` }));
  return out;
}

function validateOne(raw: unknown, index: number): BlockParseResult {
  const warnings = collectWarnings(raw);

  const partIndex =
    raw && typeof raw === 'object'
      ? ((raw as { part?: { index?: unknown } }).part?.index ?? 1)
      : 1;
  const isFirstPart = partIndex === 1;
  const schema = isFirstPart ? firstPartBlockSchema : laterPartBlockSchema;

  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    return { index, ok: false, errors: issuesFromZod(parsed.error), warnings };
  }

  const data = parsed.data;
  const block: ValidatedBlock = {
    course: data.course,
    part: data.part,
    session: data.session,
    sessionId: data.session.id,
    isFirstPart,
    flashcards: data.flashcards,
    questions: data.questions,
    problemTemplates: data.problemTemplates,
    formulas: data.reference.formulas,
    constants: data.reference.constants,
    warnings,
  };

  const errors: ImportIssue[] = [];

  // Templates are checked for meaning as well as shape: every variable must be
  // declared, the expression must evaluate cleanly across many sampled values,
  // and the constraints must be satisfiable. A template that cannot work is
  // rejected here rather than failing in front of him later.
  block.problemTemplates.forEach((template, i) => {
    const asRecord = {
      id: template.id,
      updatedAt: 0,
      deleted: false,
      course: block.course,
      sessionId: block.sessionId,
      category: template.category,
      difficulty: template.difficulty,
      order: i,
      prompt: template.prompt,
      variables: template.variables as ProblemTemplate['variables'],
      constraints: template.constraints,
      compute: template.compute,
      answerUnits: template.answerUnits,
      unitAliases: template.unitAliases,
      sigfigRule: template.sigfigRule as ProblemTemplate['sigfigRule'],
      tolerance: template.tolerance,
      steps: template.steps,
    } satisfies ProblemTemplate;

    for (const issue of validateTemplate(asRecord)) {
      errors.push({
        path: issue.path.replace(`problemTemplates[${template.id}]`, `problemTemplates[${i}]`),
        message: issue.message,
      });
    }
  });

  const seen = new Map<string, string>();
  for (const { id, path } of allIds(block)) {
    const previous = seen.get(id);
    if (previous) {
      errors.push({ path, message: `duplicates the id already used at ${previous}` });
    } else {
      seen.set(id, path);
    }
  }

  if (block.flashcards.length + block.questions.length + block.problemTemplates.length === 0) {
    if (block.formulas.length + block.constants.length === 0) {
      errors.push({ path: '(root)', message: 'contains no flashcards, questions, templates or reference entries' });
    }
  }

  if (errors.length) return { index, ok: false, errors, warnings };
  return { index, ok: true, block, errors: [], warnings };
}

function prefix(issues: ImportIssue[], index: number, many: boolean): ImportIssue[] {
  if (!many) return issues;
  return issues.map((i) => ({ ...i, path: `block ${index}: ${i.path}` }));
}

export function parseImport(input: string): ParseResult {
  const candidates = extractJsonCandidates(input);
  const results: BlockParseResult[] = [];

  if (candidates.length === 0) {
    const trimmed = input.trim();
    let message: string;
    if (!trimmed) {
      message = 'Nothing was pasted.';
    } else if (trimmed.includes('{')) {
      // An opening brace with no matching close: almost always a truncated
      // paste. Surface the parser's own complaint, which points at the end.
      const from = input.slice(input.indexOf('{'));
      try {
        safeJsonParse(from);
        message = 'No complete JSON object was found.';
      } catch (err) {
        message = `is not valid JSON — ${(err as Error).message}. The block looks cut off; paste it again including its final }.`;
      }
    } else {
      message = 'No JSON object was found. Paste the whole import block, including its outer { and }.';
    }
    return {
      results: [],
      blocks: [],
      errors: [{ path: '(root)', message }],
      warnings: [],
    };
  }

  candidates.forEach((candidate, i) => {
    let raw: unknown;
    try {
      raw = safeJsonParse(candidate);
    } catch (err) {
      results.push({
        index: i + 1,
        ok: false,
        errors: [{ path: '(root)', message: `is not valid JSON — ${(err as Error).message}` }],
        warnings: [],
      });
      return;
    }
    results.push(validateOne(raw, i + 1));
  });

  const many = results.length > 1;
  const errors = results.flatMap((r) => prefix(r.errors, r.index, many));
  const warnings = results.flatMap((r) => prefix(r.warnings, r.index, many));
  const blocks = results.filter((r) => r.ok && r.block).map((r) => r.block!);

  return { results, blocks, errors, warnings };
}
